import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { toast, Toaster } from "sonner";
import { ChevronLeft, ChevronRight, Calendar as CalIcon, Phone, ExternalLink } from "lucide-react";

const KIND_LABELS = {
  booking: "Widget Booking",
  demo_booked: "Ava Demo",
  interested: "Follow-up",
  ai_call: "AI Call",
};

function startOfMonth(d) { return new Date(d.getFullYear(), d.getMonth(), 1); }
function daysInMonth(d) { return new Date(d.getFullYear(), d.getMonth() + 1, 0).getDate(); }
function pad(n) { return String(n).padStart(2, "0"); }
function dayKey(d) { return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`; }

export default function CalendarPage() {
  const [cursor, setCursor] = useState(startOfMonth(new Date()));
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDay, setSelectedDay] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const start = new Date(cursor.getFullYear(), cursor.getMonth() - 1, 1).toISOString();
      const end = new Date(cursor.getFullYear(), cursor.getMonth() + 2, 0).toISOString();
      const { data } = await api.get(`/calendar/events?start=${encodeURIComponent(start)}&end=${encodeURIComponent(end)}`);
      setEvents(data.events || []);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [cursor]);

  const eventsByDay = useMemo(() => {
    const m = {};
    for (const e of events) {
      const d = new Date(e.starts_at);
      const k = dayKey(d);
      (m[k] ||= []).push({ ...e, _dt: d });
    }
    Object.values(m).forEach((arr) => arr.sort((a, b) => a._dt - b._dt));
    return m;
  }, [events]);

  const monthGrid = useMemo(() => {
    const firstDay = startOfMonth(cursor);
    const firstWeekday = firstDay.getDay(); // 0=Sun
    const dim = daysInMonth(cursor);
    const cells = [];
    for (let i = 0; i < firstWeekday; i++) cells.push(null);
    for (let d = 1; d <= dim; d++) cells.push(new Date(cursor.getFullYear(), cursor.getMonth(), d));
    while (cells.length % 7 !== 0) cells.push(null);
    return cells;
  }, [cursor]);

  const monthLabel = cursor.toLocaleString(undefined, { month: "long", year: "numeric" });
  const stats = useMemo(() => ({
    total: events.length,
    booked: events.filter((e) => e.kind === "demo_booked" || e.kind === "booking").length,
    calls: events.filter((e) => e.kind === "ai_call").length,
  }), [events]);

  const todayKey = dayKey(new Date());
  const selectedEvents = selectedDay ? (eventsByDay[selectedDay] || []) : [];

  return (
    <div className="p-10" data-testid="calendar-page">
      <Toaster position="top-right" richColors />
      <div className="flex items-start justify-between mb-6">
        <div>
          <div className="overline mb-2">Schedule</div>
          <h1 className="font-display text-4xl font-black tracking-tight flex items-center gap-3">
            <CalIcon className="w-8 h-8 text-[#E24E1B]" /> Calendar
          </h1>
          <p className="text-neutral-600 mt-2 max-w-2xl">
            Every appointment across the platform — Ava-booked demos, widget bookings, and AI call activity — in one view.
          </p>
        </div>
        <div className="flex gap-3">
          <StatChip label="This range" value={stats.total} />
          <StatChip label="Demos" value={stats.booked} accent="#E24E1B" />
          <StatChip label="Calls" value={stats.calls} accent="#059669" />
        </div>
      </div>

      {/* Month header */}
      <div className="flex items-center justify-between mb-4">
        <h2 className="font-display text-2xl font-bold">{monthLabel}</h2>
        <div className="flex gap-2">
          <button
            data-testid="cal-prev"
            onClick={() => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() - 1, 1))}
            className="lf-btn-outline px-3 py-1.5 flex items-center gap-1"
          ><ChevronLeft className="w-4 h-4" /> Prev</button>
          <button
            data-testid="cal-today"
            onClick={() => setCursor(startOfMonth(new Date()))}
            className="lf-btn-outline px-3 py-1.5"
          >Today</button>
          <button
            data-testid="cal-next"
            onClick={() => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() + 1, 1))}
            className="lf-btn-outline px-3 py-1.5 flex items-center gap-1"
          >Next <ChevronRight className="w-4 h-4" /></button>
        </div>
      </div>

      {/* Grid */}
      <div className="bg-white border border-neutral-200">
        <div className="grid grid-cols-7 text-xs uppercase tracking-widest font-semibold text-neutral-500 border-b border-neutral-200 bg-neutral-50">
          {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((d) => (
            <div key={d} className="p-2 text-center">{d}</div>
          ))}
        </div>
        <div className="grid grid-cols-7">
          {monthGrid.map((d, i) => {
            if (!d) return <div key={i} className="min-h-[104px] border-r border-b border-neutral-100 bg-neutral-50/40" />;
            const k = dayKey(d);
            const isToday = k === todayKey;
            const dayEvents = eventsByDay[k] || [];
            const isSelected = selectedDay === k;
            return (
              <button
                key={i}
                onClick={() => setSelectedDay(isSelected ? null : k)}
                data-testid={`cal-day-${k}`}
                className={`text-left min-h-[104px] border-r border-b border-neutral-100 p-1.5 flex flex-col hover:bg-neutral-50 transition ${isSelected ? "bg-orange-50" : ""}`}
              >
                <div className="flex justify-between items-center mb-1">
                  <span className={`text-sm font-semibold ${isToday ? "bg-[#E24E1B] text-white w-6 h-6 flex items-center justify-center rounded-full" : ""}`}>
                    {d.getDate()}
                  </span>
                  {dayEvents.length > 0 && (
                    <span className="text-[10px] font-mono text-neutral-400">{dayEvents.length}</span>
                  )}
                </div>
                <div className="space-y-0.5 overflow-hidden flex-1">
                  {dayEvents.slice(0, 3).map((e) => (
                    <div
                      key={e.id}
                      className="text-[10px] px-1.5 py-0.5 truncate text-white font-medium"
                      style={{ background: e.color || "#0F2340" }}
                      title={e.title}
                    >
                      {new Date(e.starts_at).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })} · {e.title}
                    </div>
                  ))}
                  {dayEvents.length > 3 && (
                    <div className="text-[10px] text-neutral-500 font-mono px-1">+{dayEvents.length - 3} more</div>
                  )}
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {loading && <div className="mt-4 text-neutral-500 text-sm">Loading events…</div>}

      {/* Selected day drawer */}
      {selectedDay && (
        <div className="fixed inset-0 bg-black/40 z-50 flex justify-end" onClick={() => setSelectedDay(null)}>
          <div className="w-full max-w-md bg-white h-full overflow-y-auto p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start justify-between mb-4">
              <div>
                <div className="overline mb-1">Events</div>
                <h3 className="font-display text-xl font-bold">{new Date(selectedDay).toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric", year: "numeric" })}</h3>
                <div className="text-xs text-neutral-500 mt-1">{selectedEvents.length} event{selectedEvents.length === 1 ? "" : "s"}</div>
              </div>
              <button onClick={() => setSelectedDay(null)} className="text-2xl text-neutral-400 hover:text-black">×</button>
            </div>
            {selectedEvents.length === 0 ? (
              <div className="text-neutral-500 text-sm mt-6">No events on this day.</div>
            ) : (
              <div className="space-y-3">
                {selectedEvents.map((e) => (
                  <div key={e.id} className="border-l-4 pl-3 py-2" style={{ borderColor: e.color || "#0F2340" }}>
                    <div className="flex items-baseline justify-between gap-2">
                      <span className="font-semibold">{e.title}</span>
                      <span className="text-xs text-neutral-500 font-mono">{new Date(e.starts_at).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })}</span>
                    </div>
                    <div className="text-xs text-neutral-500 mt-0.5 flex flex-wrap items-center gap-2">
                      <span className="uppercase tracking-widest font-mono">{KIND_LABELS[e.kind] || e.kind}</span>
                      {e.phone && (
                        <a href={`tel:${e.phone}`} className="flex items-center gap-1 text-blue-700 hover:underline">
                          <Phone className="w-3 h-3" /> {e.phone}
                        </a>
                      )}
                      {e.lead_id && (
                        <Link to={`/leads/${e.lead_id}`} className="flex items-center gap-1 text-neutral-700 hover:underline">
                          Lead <ExternalLink className="w-3 h-3" />
                        </Link>
                      )}
                    </div>
                    {e.preferred_time_raw && (
                      <div className="text-xs text-neutral-600 mt-1 italic">"{e.preferred_time_raw}"</div>
                    )}
                    {e.voicemail_detected && (
                      <div className="text-xs text-amber-700 mt-1">🎙 Voicemail — needs VA callback</div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function StatChip({ label, value, accent }) {
  return (
    <div className="bg-white border border-neutral-200 px-4 py-2">
      <div className="text-[10px] uppercase tracking-widest text-neutral-500 font-semibold">{label}</div>
      <div className="font-display text-2xl font-bold" style={{ color: accent || "#0F2340" }}>{value}</div>
    </div>
  );
}
